"use client"

import useSWR from "swr"
import type { BooruPost } from "@/lib/booru-api"

/**
 * Scripts-Only browse mode data source — see docs/SCRIPTS_ONLY_GRID_TASK_PLAN.md.
 *
 * Returns the same `{ posts, loading, error, hasMore }` shape as
 * useBooruSearch so BooruBrowser can swap between the two without any
 * downstream change: the grid, the filter chain, and the detail modal all
 * keep operating on plain BooruPost objects.
 *
 * Why this can't just filter the normal feed: the normal grid comes from
 * live per-source APIs (erome et al.) which have no idea Goonru scripts
 * exist, so "posts that have a script" isn't expressible as a filter on
 * that fetch. community_scripts is its own source of truth, so this mode
 * reads from there and adapts rows back into post shape.
 */

const VIDEO_EXT_REGEX = /\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)(\?|$)/i

/**
 * linked_content_id is stored inconsistently — some rows hold a bare postId,
 * others a "site:postId" string for the same post. The detail modal passes
 * the bare id to ContentScriptsPanel, so normalize to that form here or the
 * script panel comes up empty on exactly the content this mode surfaces.
 */
function normalizeLinked(rawId: unknown, rawSite: unknown): { id: string; site: string } {
  const id = typeof rawId === "string" ? rawId.trim() : ""
  const site = typeof rawSite === "string" ? rawSite.trim() : ""
  const colonIndex = id.indexOf(":")
  if (colonIndex > 0) {
    return { id: id.slice(colonIndex + 1), site: site || id.slice(0, colonIndex) }
  }
  return { id, site }
}

export interface ScriptsOnlyPost extends BooruPost {
  /** Populated only in this mode — drives the free/paid badge on the card. */
  scriptPriceCents?: number
  scriptTitle?: string
}

function adaptScriptToPost(script: any): ScriptsOnlyPost | null {
  const { id, site } = normalizeLinked(script.linked_content_id, script.linked_content_site)
  if (!id) return null

  const thumb = script.thumbnail_url || ""
  const preview = script.preview_video_url || ""
  // content_type is the script's own declaration of what it was authored
  // against; fall back to sniffing the media URL the same way the browser's
  // own getPostMediaType does, so the content-type visibility filter agrees.
  const declaredType = script.content_type === "video" ? "video" : script.content_type === "image" ? "image" : undefined
  const type = declaredType ?? (VIDEO_EXT_REGEX.test(preview || thumb) ? "video" : "image")

  return {
    id,
    site,
    // No real media URL is stored on the script row — only cached preview
    // art. fileUrl feeds media-type sniffing and the detail modal's opener,
    // which re-resolves the real post from id+site anyway.
    fileUrl: preview || thumb,
    previewUrl: thumb || preview,
    tags: Array.isArray(script.tags) ? script.tags : [],
    score: typeof script.purchase_count === "number" ? script.purchase_count : 0,
    type,
    title: script.title || undefined,
    postUrl: script.linked_content_url || undefined,
    rawPost: script,
    scriptPriceCents: typeof script.price_cents === "number" ? script.price_cents : undefined,
    scriptTitle: script.title || undefined,
  }
}

export function useScriptsOnlyPosts(enabled: boolean, page: number, limit: number) {
  const offset = Math.max(0, page) * limit
  const key = enabled ? ["scripts-only", offset, limit] : null

  const { data, error, isLoading } = useSWR<ScriptsOnlyPost[]>(
    key,
    async () => {
      const res = await fetch(
        `/api/marketplace/scripts?hasLinkedContent=true&sortBy=popular&limit=${limit}&offset=${offset}`,
      )
      if (!res.ok) throw new Error(`Scripts-only fetch failed: ${res.status}`)
      const json = await res.json()
      const scripts: any[] = Array.isArray(json.scripts) ? json.scripts : []
      return scripts.map(adaptScriptToPost).filter((p): p is ScriptsOnlyPost => p !== null)
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 1000,
      keepPreviousData: true,
      errorRetryCount: 2,
      errorRetryInterval: 1000,
    },
  )

  return {
    posts: data || [],
    loading: isLoading,
    error,
    // Matches useBooruSearch: any non-empty response means try the next page.
    // Dedupe-by-content can return fewer rows than `limit` while more pages
    // still exist, so a short page is not an end-of-feed signal.
    hasMore: data ? data.length > 0 : true,
  }
}
